import React, { useState, useEffect } from 'react';
import { getCurrentUser, fetchAuthSession } from '@aws-amplify/auth';
import { post } from 'aws-amplify/api';

const UserProfile = () => {
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',

    // Professional Links
    linkedinUrl: '',
    githubUrl: '',
    portfolioUrl: '',
    youtubeUrl: '',
    otherLinkTitle: '',
    otherLinkUrl: '',

    // Resume Content
    skills: '',
    awards: '',
    experience: '',
    education: '',
    projects: '',
    resumeText: '', // For lazy option - full resume text

    // Referral Preferences
    openToReferrals: false,
    referralCompany: ''
  });

  const [resumeOption, setResumeOption] = useState('paste'); // Default to paste option

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Expandable section states
  const [expandedSections, setExpandedSections] = useState({
    personalInfo: false,
    professionalLinks: false,
    resumeContent: false,
    referralPreferences: false
  });

  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const handleResumeOptionChange = (e) => {
    const option = e.target.value;
    setResumeOption(option);
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const { username, signInDetails } = await getCurrentUser();
        const session = await fetchAuthSession();
        const idToken = session.tokens.idToken;
        
        setFormData(prev => ({
          ...prev,
          email: idToken.payload.email,
          firstName: idToken.payload.given_name || '',
          lastName: idToken.payload.family_name || ''
        }));

        // Fetch existing profile data
        await fetchProfile(session.tokens.idToken.toString());
      } catch (error) {
        console.error('Error fetching user data:', error);
        setError('Failed to load user data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserData();
  }, []);

  const fetchProfile = async (idToken) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_GATEWAY_URL}/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'access-control-allow-headers': 'Content-Type,X-Amz-Date,Authorization,X-Api-Key,X-Amz-Security-Token,access-control-allow-headers,access-control-allow-origin,access-control-allow-methods',
          'access-control-allow-origin': '*',
          'access-control-allow-methods': 'GET,OPTIONS,PUT'
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          // Profile not found is okay for new users
          return;
        }
        throw new Error('Failed to fetch profile');
      }

      const profileData = await response.json();
      if (profileData) {
        setFormData(prev => ({
          ...prev,
          ...profileData,
          // Preserve email and names from Cognito
          email: prev.email,
          firstName: prev.firstName,
          lastName: prev.lastName
        }));
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Failed to load profile data');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens.idToken.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_GATEWAY_URL}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'access-control-allow-headers': 'Content-Type,X-Amz-Date,Authorization,X-Api-Key,X-Amz-Security-Token,access-control-allow-headers,access-control-allow-origin,access-control-allow-methods',
          'access-control-allow-origin': '*',
          'access-control-allow-methods': 'GET,OPTIONS,PUT'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      const data = await response.json();
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error saving profile:', error);
      setError('Failed to update profile. Please try again.');
      alert('Failed to update profile. Please try again.');
    }
  };

  if (isLoading) {
    return <div className="max-w-4xl mx-auto p-6">Loading...</div>;
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-8">Profile Settings</h1>
      
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Personal Information Section */}
        <section className="bg-white p-6 rounded-lg shadow">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection('personalInfo')}
          >
            <h2 className="text-xl font-semibold">Personal Information</h2>
            <svg 
              className={`w-5 h-5 transform transition-transform ${expandedSections.personalInfo ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          {expandedSections.personalInfo && (
            <div className="mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                required
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                required
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                required
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="City, Country"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
            </div>
          )}
        </section>

        {/* Professional Links Section */}
        <section className="bg-white p-6 rounded-lg shadow">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection('professionalLinks')}
          >
            <h2 className="text-xl font-semibold">Professional Links</h2>
            <svg 
              className={`w-5 h-5 transform transition-transform ${expandedSections.professionalLinks ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          {expandedSections.professionalLinks && (
            <div className="mt-4">
              <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                LinkedIn URL
              </label>
              <input
                type="url"
                name="linkedinUrl"
                value={formData.linkedinUrl}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GitHub URL
              </label>
              <input
                type="url"
                name="githubUrl"
                value={formData.githubUrl}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Portfolio URL
              </label>
              <input
                type="url"
                name="portfolioUrl"
                value={formData.portfolioUrl}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                YouTube URL
              </label>
              <input
                type="url"
                name="youtubeUrl"
                value={formData.youtubeUrl}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Other Link Description
                </label>
                <input
                  type="text"
                  name="otherLinkTitle"
                  value={formData.otherLinkTitle}
                  onChange={handleInputChange}
                  placeholder="e.g., Blog, Medium"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Other Link URL
                </label>
                <input
                  type="url"
                  name="otherLinkUrl"
                  value={formData.otherLinkUrl}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
            </div>
          )}
        </section>

        {/* Resume Content Section */}
        <section className="bg-white p-6 rounded-lg shadow">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection('resumeContent')}
          >
            <h2 className="text-xl font-semibold">Resume Content</h2>
            <svg 
              className={`w-5 h-5 transform transition-transform ${expandedSections.resumeContent ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          {expandedSections.resumeContent && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 mb-4">
                Add skills, awards, experience, education, and projects that you might want to include on your resume.
              </p>
          
          {/* Option Selection */}
          <div className="mb-6">
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="resumeOption"
                  value="paste"
                  checked={resumeOption === 'paste'}
                  onChange={handleResumeOptionChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Paste full resume (AI will process)</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="resumeOption"
                  value="individual"
                  checked={resumeOption === 'individual'}
                  onChange={handleResumeOptionChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Fill individual fields</span>
              </label>
            </div>
          </div>

          {/* Individual Fields Option */}
          <div className={`space-y-4 ${resumeOption === 'individual' ? '' : 'hidden'}`}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Skills
              </label>
              <textarea
                name="skills"
                value={formData.skills}
                onChange={handleInputChange}
                rows={3}
                placeholder="e.g., JavaScript, React, Python, AWS, Project Management"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Awards & Certifications
              </label>
              <textarea
                name="awards"
                value={formData.awards}
                onChange={handleInputChange}
                rows={3}
                placeholder="e.g., Employee of the Year 2023, AWS Certified Solutions Architect, Best Project Award"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Work Experience
              </label>
              <textarea
                name="experience"
                value={formData.experience}
                onChange={handleInputChange}
                rows={5}
                placeholder="e.g., Senior Software Engineer at Tech Corp (2020-2023)&#10;- Led development of mobile app with 1M+ users&#10;- Managed team of 5 developers&#10;- Improved system performance by 40%"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Education
              </label>
              <textarea
                name="education"
                value={formData.education}
                onChange={handleInputChange}
                rows={3}
                placeholder="e.g., Bachelor of Science in Computer Science, University of Technology (2016-2020)&#10;GPA: 3.8/4.0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Projects
              </label>
              <textarea
                name="projects"
                value={formData.projects}
                onChange={handleInputChange}
                rows={4}
                placeholder="e.g., E-commerce Platform (2023)&#10;- Built full-stack application using React and Node.js&#10;- Integrated payment processing with Stripe&#10;- Deployed on AWS with CI/CD pipeline"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Paste Resume Option */}
          <div className={`${resumeOption === 'paste' ? '' : 'hidden'}`}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Paste Your Resume
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Remove personal information (email, phone, address) before pasting. AI will extract and organize the content.
              </p>
              <textarea
                name="resumeText"
                value={formData.resumeText}
                onChange={handleInputChange}
                rows={15}
                placeholder="Paste your resume content here (without personal information)..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
            </div>
          )}
        </section>

        {/* Referral Preferences Section */}
        <section className="bg-white p-6 rounded-lg shadow">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection('referralPreferences')}
          >
            <h2 className="text-xl font-semibold">Referral Preferences</h2>
            <svg 
              className={`w-5 h-5 transform transition-transform ${expandedSections.referralPreferences ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          {expandedSections.referralPreferences && (
            <div className="mt-4">
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="openToReferrals"
                    checked={formData.openToReferrals}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-700">
                    I am open to providing referrals for this company.
                  </label>
                </div>
                {formData.openToReferrals && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company
                    </label>
                    <input
                      type="text"
                      name="referralCompany"
                      value={formData.referralCompany}
                      onChange={handleInputChange}
                      placeholder="e.g., Google, Microsoft, Amazon"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Save Profile
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserProfile; 